import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export interface FilterOption {
  key: string;
  label: string;
  options: Array<{ label: string; value: string }>;
}

interface FilterBarProps {
  filterOptions: FilterOption[];
  activeFilters: Record<string, string | string[]>;
  onSetFilter: (key: string, value: string) => void;
  onClearFilter: (key: string) => void;
  onClearAll: () => void;
  className?: string;
}

export function FilterBar({
  filterOptions,
  activeFilters,
  onSetFilter,
  onClearFilter,
  onClearAll,
  className,
}: FilterBarProps) {
  const hasActive = Object.keys(activeFilters).length > 0;

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {filterOptions.map((filter) => {
        const active = activeFilters[filter.key];
        const activeLabel = active
          ? filter.options.find((o) => o.value === active)?.label ?? String(active)
          : null;

        return (
          <DropdownMenu key={filter.key}>
            <DropdownMenuTrigger asChild>
              <Button
                variant={activeLabel ? 'secondary' : 'outline'}
                size="sm"
                className="h-8 text-xs gap-1"
              >
                {filter.label}
                {activeLabel && (
                  <Badge variant="muted" className="ml-1 px-1 text-xs">
                    {activeLabel}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-40">
              {filter.options.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={() => onSetFilter(filter.key, opt.value)}
                  data-active={active === opt.value}
                  className="data-[active=true]:font-semibold"
                >
                  {opt.label}
                </DropdownMenuItem>
              ))}
              {activeLabel && (
                <DropdownMenuItem
                  onClick={() => onClearFilter(filter.key)}
                  className="text-text-secondary"
                >
                  Clear filter
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })}

      {hasActive && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs text-text-secondary"
          onClick={onClearAll}
        >
          <X className="h-3 w-3 mr-1" />
          Clear all
        </Button>
      )}
    </div>
  );
}
